import { useState, useEffect } from "react";
import { t, font, Button } from "@realtyflows/ui";
import { aiDraftClientEmail, sendEmailApi, getClient } from "../api";

/**
 * AiEmailDraftModal — V2 Feature 2.7
 *
 * Opens when an agent clicks "Draft email" anywhere in the app. The flow:
 *   1. Generate draft (subject + body) via Claude Haiku
 *   2. Show in an editable textarea
 *   3. Agent edits → clicks Send → email goes via existing Gmail integration
 *
 * Props:
 *   clientId   — required
 *   intent     — follow_up | re_engagement | post_showing | closing | ...
 *   customNote — optional extra context to include
 *   onClose()
 *   onSent()   — optional callback after successful send
 */
const INTENT_OPTIONS = [
  { id: "follow_up", label: "Follow-up" },
  { id: "re_engagement", label: "Re-engagement (gone cold)" },
  { id: "post_showing", label: "After a showing" },
  { id: "new_listing", label: "Suggest a property" },
  { id: "pre_showing", label: "Pre-showing confirmation" },
  { id: "closing", label: "Closing / thank you" },
];

export default function AiEmailDraftModal({ clientId, intent: initialIntent = "follow_up", customNote: initialNote, onClose, onSent }) {
  const [client, setClient] = useState(null);
  const [intent, setIntent] = useState(initialIntent);
  const [customNote, setCustomNote] = useState(initialNote || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [draftLoading, setDraftLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getClient(clientId).then(setClient).catch(() => setClient(null));
  }, [clientId]);

  const generateDraft = async (intentOverride) => {
    setDraftLoading(true);
    setError("");
    try {
      const draft = await aiDraftClientEmail({
        clientId,
        intent: intentOverride || intent,
        customNote: customNote || undefined,
      });
      if (!draft) throw new Error("AI couldn't generate a draft");
      setSubject(draft.subject || "");
      setBody(draft.body || "");
    } catch (err) {
      setError(err.message || "Failed to generate draft");
    } finally {
      setDraftLoading(false);
    }
  };

  // Initial draft on mount
  useEffect(() => { generateDraft(initialIntent); /* eslint-disable-next-line */ }, []);

  const handleSend = async () => {
    if (!client?.email) {
      setError("This client doesn't have an email on file.");
      return;
    }
    setSending(true);
    setError("");
    try {
      await sendEmailApi({
        to: client.email,
        subject,
        body,
        client_id: clientId,
      });
      if (onSent) onSent();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        background: t.surface, borderRadius: 16, padding: 24,
        maxWidth: 640, width: "100%", maxHeight: "92vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              ✨ AI Email Draft
            </div>
            {client && (
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2, fontFamily: font.body }}>
                To {client.name} ({client.email || "no email on file"})
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: t.textMuted }}>×</button>
        </div>

        {/* Intent + regenerate */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={intent}
            onChange={(e) => { setIntent(e.target.value); }}
            style={{
              fontSize: 12, padding: "6px 10px", borderRadius: 6,
              border: `1.5px solid ${t.border}`, background: t.surfaceWarm,
              color: t.textPrimary, fontFamily: font.body, outline: "none",
            }}
          >
            {INTENT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <input
            placeholder="Extra context (optional)"
            value={customNote}
            onChange={(e) => setCustomNote(e.target.value)}
            style={{
              flex: 1, minWidth: 200,
              fontSize: 12, padding: "6px 10px", borderRadius: 6,
              border: `1.5px solid ${t.border}`, background: t.surfaceWarm,
              color: t.textPrimary, fontFamily: font.body, outline: "none",
            }}
          />
          <Button variant="outline" size="sm" onClick={() => generateDraft()} disabled={draftLoading}>
            {draftLoading ? "Drafting..." : "Regenerate"}
          </Button>
        </div>

        {/* Subject */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, fontFamily: font.mono, letterSpacing: 0.5, textTransform: "uppercase" }}>
            Subject
          </label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={draftLoading}
            style={{
              width: "100%", fontSize: 13, padding: "8px 12px",
              border: `1.5px solid ${t.border}`, borderRadius: 6,
              background: t.surfaceWarm, color: t.textPrimary, fontFamily: font.body, outline: "none",
            }}
          />
        </div>

        {/* Body */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, fontFamily: font.mono, letterSpacing: 0.5, textTransform: "uppercase" }}>
            Message
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={draftLoading}
            rows={12}
            style={{
              width: "100%", fontSize: 13, padding: 12,
              border: `1.5px solid ${t.border}`, borderRadius: 6,
              background: t.surfaceWarm, color: t.textPrimary, fontFamily: font.body,
              outline: "none", resize: "vertical", lineHeight: 1.5,
            }}
          />
        </div>

        {error && (
          <div style={{ fontSize: 12, color: t.coral, marginBottom: 12, fontFamily: font.body }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSend} disabled={sending || draftLoading || !subject || !body}>
            {sending ? "Sending..." : "Send email"}
          </Button>
        </div>
      </div>
    </div>
  );
}
